/**
 * Sign-out confirmation popover. Used by AppShell's SignOutNavItem
 * (sidebar + mobile tab-bar), Settings's SignOutRow, and Builder's
 * header sign-out button.
 *
 * Rendered as a small contextual popover anchored to the trigger
 * (not a full-page modal). Portalled to document.body so it always
 * paints on top of every other element + isn't clipped by a parent's
 * stacking context (the sidebar uses position:sticky which had been
 * burying the previous full-page modal under the main content).
 *
 * Position is computed from the trigger button's bounding rect on
 * open; the popover prefers to render directly above the trigger,
 * right-aligned. Falls below when there's no room above.
 */
import { ReactNode, RefObject, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Btn } from './Btn';

/** Imperative sign-out — Convex Auth's signOut owns token cleanup. */
export function useSignOutAction(): () => Promise<void> {
  const navigate = useNavigate();
  return useCallback(async () => {
    try { await api.signout(); } catch { /* best-effort */ }
    // Convex Auth's signOut() (invoked inside api.signout) clears its
    // own storage.
    navigate('/', { replace: true });
  }, [navigate]);
}

/** Open/close state for a single confirm popover. */
export function useSignOutConfirm() {
  const [open, setOpen] = useState(false);
  return {
    open,
    request: () => setOpen(true),
    cancel: () => setOpen(false),
  };
}

type Coords = { top: number; left: number; placement: 'above' | 'below' };

const POPOVER_W = 240;
const POPOVER_H_ESTIMATE = 110; // height the popover roughly needs

function placeAgainst(trigger: HTMLElement): Coords {
  const r = trigger.getBoundingClientRect();
  const margin = 8;
  const viewportH = window.innerHeight;
  const viewportW = window.innerWidth;

  // Prefer above; fall back below if no room.
  const placement: 'above' | 'below' =
    r.top - POPOVER_H_ESTIMATE - margin >= 0 ? 'above' : 'below';
  const top = placement === 'above' ? r.top - POPOVER_H_ESTIMATE - margin : r.bottom + margin;

  // Right-align the popover to the trigger, but never let it spill
  // off the left or right edge.
  let left = r.right - POPOVER_W;
  if (left < 8) left = 8;
  if (left + POPOVER_W > viewportW - 8) left = viewportW - 8 - POPOVER_W;

  // Final vertical clamp — in case the placement computation undershot.
  let safeTop = top;
  if (safeTop < 8) safeTop = 8;
  if (safeTop + POPOVER_H_ESTIMATE > viewportH - 8) safeTop = viewportH - 8 - POPOVER_H_ESTIMATE;

  return { top: safeTop, left, placement };
}

export function SignOutConfirm({
  open, onCancel, triggerRef, children,
}: {
  open: boolean;
  onCancel: () => void;
  triggerRef: RefObject<HTMLElement | null>;
  /** Optional message override — defaults to "Are you sure you want to sign out?". */
  children?: ReactNode;
}) {
  const doSignOut = useSignOutAction();
  const [coords, setCoords] = useState<Coords | null>(null);

  // Compute placement when opening + on scroll/resize while open.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) { setCoords(null); return; }
    const update = () => {
      if (triggerRef.current) setCoords(placeAgainst(triggerRef.current));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, triggerRef]);

  // Click-outside + Escape close.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      // Ignore clicks inside the popover or on the trigger itself.
      if (
        target instanceof Element &&
        (target.closest('[data-signout-popover]') ||
         (triggerRef.current && triggerRef.current.contains(target)))
      ) return;
      onCancel();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onCancel, triggerRef]);

  if (!open || !coords) return null;

  const node = (
    <div
      data-signout-popover
      role="dialog"
      aria-modal="false"
      aria-label="Sign out confirmation"
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        width: POPOVER_W,
        background: 'var(--surface)',
        borderRadius: 14,
        boxShadow: 'var(--shadow-lift)',
        border: '1px solid var(--line-strong)',
        padding: 14,
        zIndex: 1000,
        animation: 'scale-in 0.12s ease',
        transformOrigin: coords.placement === 'above' ? '100% 100%' : '100% 0%',
      }}
    >
      <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.45, marginBottom: 12 }}>
        {children ?? 'Are you sure you want to sign out?'}
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <Btn variant="ghost" size="sm" onClick={onCancel}>Cancel</Btn>
        <Btn variant="primary" size="sm" onClick={doSignOut}>Sign out</Btn>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
